// Cuadro «Cómo originamos» (C5) del mockup: la mezcla por nivel de riesgo a la izquierda y el
// scatter de TIENDAS con sus cuadrantes a la derecha, dentro de la misma tarjeta.
//
// 🔴 «Empleado» NO es un nivel: va contado aparte, fuera de las barras y fuera del SCI promedio. Es
// un tipo de venta con plan propio, y pintarlo como cuarta barra le inventaría un nivel de riesgo.
import type { OriginationRiskMix, StoreCohortPoint } from '@/api/services/retail-wizard/backoffice';
import { cn } from '@/lib/cn';
import {
    formatPct,
    formatUnits,
    orderRiskLevels,
    riskBarSegments,
    riskLevelColor,
    riskLevelLabel,
    riskLevelTextColor,
} from '../lib/originationMetrics';
import { DownPaymentDeltaChip } from './OriginationDeltas';
import { OriginationStoreScatter } from './OriginationStoreScatter';

const SECTION_HEAD = 'text-[9.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground';

/** La barra apilada por nivel (`.conv-bar` del mock), en el orden Bronce · Plata · Oro. */
function RiskBar({ mix }: { mix: OriginationRiskMix }) {
    const segments = riskBarSegments(mix);
    if (segments.length === 0) return null;
    const summary = segments
        .map((seg) => `${seg.label}: ${formatUnits(seg.credits)} (${seg.sharePct.toFixed(0)}%)`)
        .join(' · ');
    return (
        <div
            className="flex h-8 flex-none overflow-hidden rounded border border-border"
            role="img"
            aria-label={`Mezcla por nivel de riesgo — ${summary}`}
        >
            {segments.map((seg) => (
                <div
                    key={seg.level}
                    className="flex min-w-0 items-center justify-center overflow-hidden whitespace-nowrap border-r border-card text-[11px] font-bold last:border-r-0"
                    style={{
                        flex: seg.credits,
                        backgroundColor: riskLevelColor(seg.level),
                        color: riskLevelTextColor(seg.level),
                    }}
                    title={`${seg.label} · ${formatUnits(seg.credits)} créditos`}
                >
                    {seg.showLabel ? `${seg.sharePct.toFixed(0)}%` : ''}
                </div>
            ))}
        </div>
    );
}

/** Una tarjeta de la leyenda: el nivel, sus créditos y el enganche que cobró contra su mínimo. */
function RiskLegendItem({
    level,
    credits,
    downPaymentAvgPct,
    minDownPaymentAvgPct,
    downPaymentOverMinPp,
}: {
    level: string;
    credits: number;
    downPaymentAvgPct: number | null;
    minDownPaymentAvgPct: number | null;
    downPaymentOverMinPp: number | null;
}) {
    return (
        <div
            className="grid grid-cols-[60px_1fr] items-center gap-2 rounded border-l-[3px] bg-muted/40 px-2 py-[5px]"
            style={{ borderLeftColor: riskLevelColor(level) }}
        >
            <div className="min-w-0">
                <div className={SECTION_HEAD}>{riskLevelLabel(level)}</div>
                <div
                    className={cn(
                        'text-[13px] font-semibold leading-[1.05] tabular-nums',
                        credits === 0 && 'text-muted-foreground',
                    )}
                >
                    {formatUnits(credits)}
                </div>
            </div>
            <div className="min-w-0 text-right">
                {credits === 0 || downPaymentAvgPct === null ? (
                    <div className="text-[10px] leading-[1.25] text-muted-foreground">
                        {credits === 0 ? 'sin créditos' : 'sin enganche registrado'}
                    </div>
                ) : (
                    <>
                        <div className="text-[10px] leading-[1.25] text-foreground">
                            Eng. {formatPct(downPaymentAvgPct)}
                            <DownPaymentDeltaChip overMinPp={downPaymentOverMinPp} />
                        </div>
                        {/* El mínimo solo se escribe si el catálogo lo dio: sin plan no hay mínimo,
                            y un «mín. 0%» diría que el plan no exigía enganche. */}
                        {minDownPaymentAvgPct === null ? null : (
                            <div className="text-[10px] leading-[1.25] text-muted-foreground opacity-75">
                                mín. {formatPct(minDownPaymentAvgPct)}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/** El cuerpo del cuadro C5: niveles a la izquierda, scatter de tiendas a la derecha. */
export function OriginationRiskPanel({
    mix,
    storeScatter,
    selectedStoreId,
    onSelectStore,
}: {
    mix: OriginationRiskMix;
    storeScatter: StoreCohortPoint[];
    selectedStoreId: string | null;
    onSelectStore: (storeId: string) => void;
}) {
    const levels = orderRiskLevels(mix.levels);
    return (
        <div className="grid min-h-0 flex-1 grid-cols-[40%_60%]">
            <div className="flex min-h-0 flex-col gap-2 overflow-y-auto border-r border-border px-3 py-2.5">
                <div className={SECTION_HEAD}>Por nivel de riesgo</div>
                <RiskBar mix={mix} />
                <div className="flex flex-col gap-[5px]">
                    {levels.map((cell) => (
                        <RiskLegendItem
                            key={cell.level}
                            level={cell.level}
                            credits={cell.credits}
                            downPaymentAvgPct={cell.downPaymentAvgPct}
                            minDownPaymentAvgPct={cell.minDownPaymentAvgPct}
                            downPaymentOverMinPp={cell.downPaymentOverMinPp}
                        />
                    ))}
                </div>
                {/* Los dos conteos que NO son un nivel. Solo se pintan si existen: un «0 empleados»
                    ocuparía el lugar sin decir nada. */}
                {mix.employeeCredits > 0 ? (
                    <p className="rounded bg-muted/35 px-2 py-1.5 text-[10px] text-muted-foreground">
                        <strong className="font-bold text-foreground">
                            + {formatUnits(mix.employeeCredits)}{' '}
                            {mix.employeeCredits === 1 ? 'empleado' : 'empleados'}
                        </strong>{' '}
                        — venta con plan propio, fuera de las barras y del SCI promedio.
                    </p>
                ) : null}
                {mix.unknownLevelCredits > 0 ? (
                    <p className="rounded bg-muted/35 px-2 py-1.5 text-[10px] text-muted-foreground">
                        <strong className="font-bold text-foreground">
                            + {formatUnits(mix.unknownLevelCredits)} sin nivel
                        </strong>{' '}
                        — el crédito no guardó el nivel al originarse; no se les inventa uno.
                    </p>
                ) : null}
            </div>
            <div className="flex min-h-0 flex-col">
                {storeScatter.length === 0 ? (
                    <p className="p-3 text-center text-[11px] italic leading-relaxed text-muted-foreground">
                        Ninguna tienda de la ventana trae SCI y cosecha juntos: no hay nube que
                        dibujar.
                    </p>
                ) : (
                    <OriginationStoreScatter
                        points={storeScatter}
                        selectedStoreId={selectedStoreId}
                        onSelectStore={onSelectStore}
                    />
                )}
            </div>
        </div>
    );
}
